import React, { useState, useEffect } from 'react';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { z } from 'zod';
import {
  Form,
  FormField,
  Input,
  OptionSheet,
  PhoneInput,
  Textarea,
  applyServerErrors,
  useZodForm,
} from '@relay-towing/ui';
import Header from '@/components/common/Header';
import { useCreateCall } from '@/hooks/useCreateCall';
import { useDriverStateV2 } from '@/hooks/useDriverStateV2';
import { useGetCustomers } from '@/api/hooks/useGetCustomers';
import { useGetAllDrivers } from '@/api/hooks/useGetAllDrivers';
import { useGetVehicleMakes } from '@/api/hooks/useGetVehicleMakes';
import { useGetVehicleModels } from '@/api/hooks/useGetVehicleModels';
import { CustomAutocomplete } from '@/components/ui/CustomAutocomplete';
import { useLoadScript } from '@react-google-maps/api';
import { getDriverDisplayName } from '@/utils/driverName';

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
// Static array to prevent unnecessary LoadScript reloads
const GOOGLE_MAPS_LIBS: 'places'[] = ['places'];

const PRIORITY_OPTIONS = [
  { value: 'LOW', label: 'Low' },
  { value: 'NORMAL', label: 'Normal' },
  { value: 'HIGH', label: 'High' },
  { value: 'URGENT', label: 'Urgent' },
];

/**
 * An account and a pickup location are what a call cannot go without; the
 * rest of the record can be filled in as the job runs. The screen used to
 * say so twice — an asterisk on each label, and a `canSubmit` that greyed
 * the button out without saying why.
 */
const createCallSchema = z.object({
  account: z.string().trim().min(1, 'Account is required'),
  priority: z.enum(['LOW', 'NORMAL', 'HIGH', 'URGENT']).default('NORMAL'),
  dispatchNumber: z.string(),
  contactName: z.string(),
  contactPhone: z.string(),
  pickupLocation: z.string().trim().min(1, 'Pickup location is required'),
  destination: z.string(),
  vehicleMake: z.string(),
  vehicleModel: z.string(),
  vehicleYear: z.string(),
  vehicleColor: z.string(),
  drivers: z.array(z.string()).default([]),
  notes: z.string(),
});

type CreateCallValues = z.infer<typeof createCallSchema>;

/** Filled in by Places when a suggestion is picked, cleared when typed over. */
interface CallCoords {
  pickupLocationLat?: number;
  pickupLocationLong?: number;
  destinationLat?: number;
  destinationLong?: number;
}

interface CreateCallScreenProps {
  onBack: () => void;
  onSuccess?: () => void;
  showHeader?: boolean;
}

const CreateCallScreen: React.FC<CreateCallScreenProps> = ({
  onBack,
  onSuccess,
  showHeader = true,
}) => {
  const { driver } = useDriverStateV2();
  const { mutate: createCall, isPending } = useCreateCall();
  const { data: customersData } = useGetCustomers();
  const { data: driversData } = useGetAllDrivers();
  const { data: vehicleMakesData } = useGetVehicleMakes();
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBS,
  });

  // Generate a dispatch number based on timestamp and random number
  const generateDispatchNumber = () => {
    const timestamp = Date.now().toString().slice(-6);
    const random = Math.random().toString(36).substring(2, 5).toUpperCase();
    return `${timestamp}${random}`;
  };

  // Get the first customer as default
  const defaultAccount = customersData?.data?.[0]?.id || '';

  const form = useZodForm(createCallSchema, {
    defaultValues: {
      account: '',
      priority: 'NORMAL',
      dispatchNumber: generateDispatchNumber(),
      contactName: '',
      contactPhone: '',
      pickupLocation: '',
      destination: '',
      vehicleMake: '',
      vehicleModel: '',
      vehicleYear: '',
      vehicleColor: '',
      drivers: [],
      notes: '',
    },
  });

  /* Coordinates come from the place that was picked, not from typing, so
     they sit beside the form rather than in it. */
  const [coords, setCoords] = useState<CallCoords>({});

  const vehicleMake = form.watch('vehicleMake');
  const selectedDrivers = form.watch('drivers');

  // Get vehicle models based on selected make
  const { data: vehicleModelsData } = useGetVehicleModels(
    { make: vehicleMake },
    { enabled: !!vehicleMake }
  );

  // Set default account when customers data loads
  useEffect(() => {
    if (defaultAccount && !form.getValues('account')) {
      form.setValue('account', defaultAccount);
    }
  }, [defaultAccount, form]);

  const handleSubmit = (values: CreateCallValues) => {
    const driversToSubmit =
      values.drivers.length > 0
        ? values.drivers
        : ([driver?.id].filter(Boolean) as string[]);

    createCall(
      {
        ...values,
        ...coords,
        vehicleYear: values.vehicleYear
          ? parseInt(values.vehicleYear)
          : undefined,
        // Use selected drivers if any are selected, otherwise include current driver
        drivers: driversToSubmit,
      },
      {
        onSuccess: () => {
          onSuccess?.();
        },
        onError: (error) =>
          applyServerErrors(
            form,
            error,
            error instanceof Error ? error.message : 'Failed to create call'
          ),
      }
    );
  };

  return (
    <div className="min-h-screen bg-primary responsive-container mx-auto flex flex-col">
      {/* Header */}
      {showHeader && (
        <Header
          driverName={getDriverDisplayName(driver, 'Driver')}
          status={driver?.status || 'unknown'}
          onMenuClick={() => {}}
          onStatusClick={() => {}}
          onStatusChange={() => {}}
          isEmbeddedMode={false}
        />
      )}

      {/* Content */}
      <div
        style={{ backgroundColor: '#FFFFFF' }}
        className="z-30 flex-1 rounded-t-3xl overflow-y-auto pb-20"
      >
        {/* Back Button */}
        <div
          style={{ backgroundColor: '#FFFFFF', borderColor: '#E5E7EB' }}
          className="sticky top-0 border-b p-4"
        >
          <button
            onClick={onBack}
            className="flex items-center gap-2 transition-colors"
            style={{ color: '#4B5563' }}
            onMouseEnter={(e) => (e.currentTarget.style.color = '#1A202C')}
            onMouseLeave={(e) => (e.currentTarget.style.color = '#4B5563')}
          >
            <ArrowLeft size={24} />
            <span>Create Call</span>
          </button>
        </div>

        {/* Form */}
        <Form form={form} gap={4} onSubmit={handleSubmit} className="p-4">
          {/* Account Selection */}
          <FormField form={form} name="account" label="Account">
            {(field) => (
              <OptionSheet
                title="Account"
                searchable
                options={(customersData?.data ?? []).map((customer) => ({
                  value: customer.id,
                  label: customer.accountName,
                }))}
                placeholder="Select an account"
                value={field.value || undefined}
                onValueChange={field.onChange}
              />
            )}
          </FormField>

          {/* Contact Name and Phone Row */}
          <div className="grid grid-cols-2 gap-4">
            <FormField form={form} name="contactName" label="Contact Name">
              {(field) => <Input {...field} placeholder="Enter name" />}
            </FormField>
            <FormField form={form} name="contactPhone" label="Contact Phone">
              {(field) => <PhoneInput {...field} placeholder="Enter phone" />}
            </FormField>
          </div>

          {/* Pickup Location */}
          <FormField form={form} name="pickupLocation" label="Pickup Location">
            {(field) =>
              isLoaded ? (
                <CustomAutocomplete
                  value={field.value}
                  onChange={(value) => {
                    field.onChange(value);
                    setCoords((prev) => ({
                      ...prev,
                      pickupLocationLat: undefined,
                      pickupLocationLong: undefined,
                    }));
                  }}
                  onPlaceSelected={(place) => {
                    field.onChange(place.formatted_address);
                    setCoords((prev) => ({
                      ...prev,
                      pickupLocationLat: place.geometry.location.lat,
                      pickupLocationLong: place.geometry.location.lng,
                    }));
                  }}
                  placeholder="Enter pickup location"
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              ) : (
                <Input {...field} placeholder="Enter pickup location" />
              )
            }
          </FormField>

          {/* Dropoff Location*/}
          <FormField form={form} name="destination" label="Dropoff Location">
            {(field) =>
              isLoaded ? (
                <CustomAutocomplete
                  value={field.value}
                  onChange={(value) => {
                    field.onChange(value);
                    setCoords((prev) => ({
                      ...prev,
                      destinationLat: undefined,
                      destinationLong: undefined,
                    }));
                  }}
                  onPlaceSelected={(place) => {
                    field.onChange(place.formatted_address);
                    setCoords((prev) => ({
                      ...prev,
                      destinationLat: place.geometry.location.lat,
                      destinationLong: place.geometry.location.lng,
                    }));
                  }}
                  placeholder="Enter dropoff location"
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              ) : (
                <Input {...field} placeholder="Enter dropoff location" />
              )
            }
          </FormField>

          {/* Vehicle Make and Model Row */}
          <div className="grid grid-cols-2 gap-4">
            <FormField form={form} name="vehicleMake" label="Vehicle Make">
              {(field) => (
                <OptionSheet
                  title="Vehicle Make"
                  searchable
                  options={(vehicleMakesData ?? []).map((make) => ({
                    value: make.make,
                    label: make.make,
                  }))}
                  placeholder="Select Make"
                  value={field.value || undefined}
                  onValueChange={(value) => {
                    field.onChange(value);
                    // Reset model when make changes
                    form.setValue('vehicleModel', '');
                  }}
                />
              )}
            </FormField>

            <FormField form={form} name="vehicleModel" label="Vehicle Model">
              {(field) => (
                <OptionSheet
                  title="Vehicle Model"
                  searchable
                  options={(vehicleModelsData ?? []).map((model) => ({
                    value: model.model,
                    label: model.model,
                  }))}
                  placeholder="Select Model"
                  disabled={!vehicleMake}
                  value={field.value || undefined}
                  onValueChange={field.onChange}
                />
              )}
            </FormField>
          </div>

          {/* Vehicle Year and Color Row */}
          <div className="grid grid-cols-2 gap-4">
            <FormField form={form} name="vehicleYear" label="Vehicle Year">
              {(field) => (
                <Input
                  {...field}
                  onChange={(e) =>
                    field.onChange(
                      e.target.value.replace(/\D/g, '').slice(0, 4)
                    )
                  }
                  placeholder="e.g. 2024"
                />
              )}
            </FormField>

            <FormField form={form} name="vehicleColor" label="Vehicle Color">
              {(field) => (
                <Input {...field} placeholder="e.g. Red" maxLength={16} />
              )}
            </FormField>
          </div>

          {/* Priority */}
          <FormField form={form} name="priority" label="Priority">
            {(field) => (
              <OptionSheet
                title="Priority"
                options={PRIORITY_OPTIONS}
                value={field.value}
                onValueChange={field.onChange}
              />
            )}
          </FormField>

          {/* Assign Drivers - Mobile Optimized */}
          <div>
            <div className="flex justify-between items-center mb-3">
              <label
                style={{ color: '#374151' }}
                className="block text-sm font-medium"
              >
                Assign Drivers
              </label>
              <span style={{ color: '#6B7280' }} className="text-xs">
                {selectedDrivers.length} selected
              </span>
            </div>
            <div
              className="grid grid-cols-2 gap-3 max-h-96 overflow-y-auto border rounded-lg p-3"
              style={{ borderColor: '#E5E7EB', backgroundColor: '#FAFAFA' }}
            >
              {driversData && driversData.length > 0 ? (
                driversData.map((companyDriver) => {
                  const userId = companyDriver.user?.id;
                  const isSelected = userId
                    ? selectedDrivers.includes(userId)
                    : false;
                  return (
                    <div
                      key={companyDriver.id}
                      onClick={() => {
                        if (!userId) return;
                        form.setValue(
                          'drivers',
                          isSelected
                            ? selectedDrivers.filter((d) => d !== userId)
                            : [...selectedDrivers, userId]
                        );
                      }}
                      style={{
                        backgroundColor: isSelected ? '#E0F2FE' : '#FFFFFF',
                        borderColor: isSelected ? '#0284C7' : '#E5E7EB',
                      }}
                      className="p-2 border rounded-lg cursor-pointer transition-colors hover:bg-gray-50"
                    >
                      <div className="flex items-start gap-2">
                        <div
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!userId) return;
                            form.setValue(
                              'drivers',
                              isSelected
                                ? selectedDrivers.filter((d) => d !== userId)
                                : [...selectedDrivers, userId]
                            );
                          }}
                          style={{
                            marginTop: '2px',
                            width: '18px',
                            height: '18px',
                            minWidth: '18px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: isSelected ? '#45A17D' : '#FFFFFF',
                            border: '2px solid #45A17D',
                            borderRadius: '3px',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                          }}
                        >
                          {isSelected && (
                            <span
                              style={{
                                color: '#FFFFFF',
                                fontSize: '12px',
                                fontWeight: 'bold',
                              }}
                            >
                              ✓
                            </span>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p
                            style={{ color: '#1F2937' }}
                            className="text-xs font-medium truncate"
                          >
                            {companyDriver.name}
                          </p>
                          <div className="flex items-center gap-1 mt-1 flex-wrap">
                            <span
                              style={{
                                fontSize: '10px',
                                padding: '2px 8px',
                                borderRadius: '12px',
                                color:
                                  companyDriver.status === 'ACTIVE'
                                    ? '#059669'
                                    : '#6B7280',
                                backgroundColor:
                                  companyDriver.status === 'ACTIVE'
                                    ? '#D1FAE5'
                                    : '#F3F4F6',
                              }}
                            >
                              {companyDriver.status}
                            </span>
                            {companyDriver.truckNumber &&
                              !companyDriver.truckNumber.startsWith('TEMP') && (
                                <span
                                  style={{ color: '#6B7280', fontSize: '10px' }}
                                >
                                  Truck {companyDriver.truckNumber}
                                </span>
                              )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <p
                  style={{ color: '#9CA3AF' }}
                  className="text-sm text-center py-4 col-span-2"
                >
                  No drivers available
                </p>
              )}
            </div>
          </div>

          {/* Notes */}
          <FormField form={form} name="notes" label="Notes">
            {(field) => (
              <Textarea {...field} placeholder="Add any notes..." rows={3} />
            )}
          </FormField>

          {/* Dispatch Call Button */}
          <button
            type="submit"
            disabled={isPending}
            style={
              isPending
                ? {
                    backgroundColor: '#D1D5DB',
                    color: '#9CA3AF',
                    cursor: 'not-allowed',
                  }
                : {
                    backgroundColor: '#45A17D',
                    color: '#FFFFFF',
                  }
            }
            className="w-full py-3 px-4 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors"
          >
            {isPending && <Loader2 size={20} className="animate-spin" />}
            {isPending ? 'Creating Call...' : 'Dispatch Call'}
          </button>
        </Form>
      </div>
    </div>
  );
};

export default CreateCallScreen;
